import { Component, OnInit, OnDestroy, Inject, ViewEncapsulation } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Lang, resolveLanguageUrls } from './language-routes';

/**
 * Two-state language control for the top bar.
 *
 * Replaces the Bootstrap-collapse dropdown that was duplicated across the
 * `*ngIf` / `#arSwitch` branches of raksme.secBarView.html. Both destinations
 * are derived from the live route (see language-routes.ts) rather than left to
 * relative `routerLink` strings, and both labels are static so the control is
 * complete on first paint with no skeleton loader.
 */
@Component({
  selector: 'raksme-language-switcher',
  templateUrl: './language-switcher.component.html',
  styleUrls: ['./language-switcher.component.css'],
  // Unscoped so the RTL and :has() hover-preview rules reach the markup as
  // written; every selector is prefixed with the unique .rak-lang-switch.
  encapsulation: ViewEncapsulation.None
})
export class LanguageSwitcherComponent implements OnInit, OnDestroy {

  public lang: Lang = 'en';
  public enUrl: string = '/';
  public arUrl: string = '/ar';

  private routerSub: Subscription;

  constructor(private router: Router, @Inject(DOCUMENT) private document: any) {}

  ngOnInit() {
    this.sync(this.router.url);

    // The header is re-created per page today, but the switcher must not depend
    // on that: keep it correct across in-app navigations too.
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => this.sync(event.urlAfterRedirects));
  }

  ngOnDestroy() {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  private sync(url: string) {
    const resolved = resolveLanguageUrls(url);
    this.lang = resolved.lang;
    this.enUrl = resolved.enUrl;
    this.arUrl = resolved.arUrl;
    this.applyDocumentLanguage();
  }

  /**
   * `<html>` previously stayed `lang="en" dir="ltr"` on Arabic pages; RTL came
   * only from `direction: rtl` in CSS via `langClass`, so assistive tech and
   * text selection got no signal that the page is RTL.
   */
  private applyDocumentLanguage() {
    const root = this.document && this.document.documentElement;
    if (!root) {
      return;
    }
    root.setAttribute('lang', this.lang);
    root.setAttribute('dir', this.lang === 'ar' ? 'rtl' : 'ltr');
  }
}
